/**
 * Workshop Progress Tracker
 * Persists workshop state so users can resume later
 */

export interface WorkshopProgress {
  workshopId: string;
  workshopName: string;
  currentStepIndex: number;
  selectedOptions: Record<string, string>; // stepId -> optionId
  score: number;
  completedSteps: string[];
  startedAt: string;
  lastUpdatedAt: string;
  completedAt?: string;
  finalScore?: number;
  status: 'in-progress' | 'completed';
}

export interface WorkshopAttempt {
  id: string;
  workshopId: string;
  workshopName: string;
  completedAt: string;
  finalScore: number;
  stepResults: Array<{
    stepId: string;
    stage: string;
    selectedOption: string;
    score: number;
  }>;
}

class WorkshopProgressTracker {
  private readonly STORAGE_KEY_PROGRESS = 'peakPriorities_workshopProgress';
  private readonly STORAGE_KEY_ATTEMPTS = 'peakPriorities_workshopAttempts';

  /**
   * Get current progress for a workshop
   */
  getProgress(workshopId: string): WorkshopProgress | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY_PROGRESS);
      if (!stored) return null;

      const allProgress: Record<string, WorkshopProgress> = JSON.parse(stored);
      return allProgress[workshopId] || null;
    } catch (error) {
      console.error('Failed to load workshop progress:', error);
      return null;
    }
  }

  /**
   * Save progress for a workshop
   */
  saveProgress(progress: WorkshopProgress): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY_PROGRESS);
      const allProgress: Record<string, WorkshopProgress> = stored ? JSON.parse(stored) : {};
      
      allProgress[progress.workshopId] = {
        ...progress,
        lastUpdatedAt: new Date().toISOString(),
      };

      localStorage.setItem(this.STORAGE_KEY_PROGRESS, JSON.stringify(allProgress));
    } catch (error) {
      console.error('Failed to save workshop progress:', error);
    }
  }

  /**
   * Complete a workshop and save as attempt
   */
  completeWorkshop(
    workshopId: string,
    workshopName: string,
    finalScore: number,
    stepResults: WorkshopAttempt['stepResults']
  ): void {
    try {
      // Create attempt record
      const attempt: WorkshopAttempt = {
        id: `attempt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        workshopId,
        workshopName,
        completedAt: new Date().toISOString(),
        finalScore,
        stepResults,
      };

      // Save to attempts history
      const storedAttempts = localStorage.getItem(this.STORAGE_KEY_ATTEMPTS);
      const attempts: WorkshopAttempt[] = storedAttempts ? JSON.parse(storedAttempts) : [];
      attempts.push(attempt);
      localStorage.setItem(this.STORAGE_KEY_ATTEMPTS, JSON.stringify(attempts));

      // Mark progress as completed
      const stored = localStorage.getItem(this.STORAGE_KEY_PROGRESS);
      const allProgress: Record<string, WorkshopProgress> = stored ? JSON.parse(stored) : {};
      
      if (allProgress[workshopId]) {
        allProgress[workshopId].status = 'completed';
        allProgress[workshopId].completedAt = new Date().toISOString();
        allProgress[workshopId].finalScore = finalScore;
        localStorage.setItem(this.STORAGE_KEY_PROGRESS, JSON.stringify(allProgress));
      }
    } catch (error) {
      console.error('Failed to complete workshop:', error);
    }
  }

  /**
   * Get all attempts for a workshop
   */
  getAttempts(workshopId: string): WorkshopAttempt[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY_ATTEMPTS);
      if (!stored) return [];

      const allAttempts: WorkshopAttempt[] = JSON.parse(stored);
      return allAttempts
        .filter(a => a.workshopId === workshopId)
        .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
    } catch (error) {
      console.error('Failed to load workshop attempts:', error);
      return [];
    }
  }

  /**
   * Get best score for a workshop
   */
  getBestScore(workshopId: string): number | null {
    const attempts = this.getAttempts(workshopId);
    if (attempts.length === 0) return null;
    return Math.max(...attempts.map(a => a.finalScore));
  }

  /**
   * Get total attempts count
   */
  getAttemptsCount(workshopId: string): number {
    return this.getAttempts(workshopId).length;
  }

  /**
   * Reset/clear progress for a workshop
   */
  resetProgress(workshopId: string): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY_PROGRESS);
      if (!stored) return;

      const allProgress: Record<string, WorkshopProgress> = JSON.parse(stored);
      delete allProgress[workshopId];
      localStorage.setItem(this.STORAGE_KEY_PROGRESS, JSON.stringify(allProgress));
    } catch (error) {
      console.error('Failed to reset workshop progress:', error);
    }
  }

  /**
   * Get all in-progress workshops
   */
  getAllInProgress(): WorkshopProgress[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY_PROGRESS);
      if (!stored) return [];

      const allProgress: Record<string, WorkshopProgress> = JSON.parse(stored);
      return Object.values(allProgress)
        .filter(p => p.status === 'in-progress')
        .sort((a, b) => new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime());
    } catch (error) {
      console.error('Failed to load in-progress workshops:', error);
      return [];
    }
  }

  /**
   * Get stats
   */
  getStats() {
    try {
      const progressStored = localStorage.getItem(this.STORAGE_KEY_PROGRESS);
      const attemptsStored = localStorage.getItem(this.STORAGE_KEY_ATTEMPTS);

      const allProgress: Record<string, WorkshopProgress> = progressStored ? JSON.parse(progressStored) : {};
      const allAttempts: WorkshopAttempt[] = attemptsStored ? JSON.parse(attemptsStored) : [];

      const inProgress = Object.values(allProgress).filter(p => p.status === 'in-progress').length;
      const completed = Object.values(allProgress).filter(p => p.status === 'completed').length;

      return {
        inProgress,
        completed,
        totalAttempts: allAttempts.length,
        averageScore: allAttempts.length > 0
          ? Math.round(allAttempts.reduce((sum, a) => sum + a.finalScore, 0) / allAttempts.length)
          : 0,
      };
    } catch (error) {
      console.error('Failed to get workshop stats:', error);
      return {
        inProgress: 0,
        completed: 0,
        totalAttempts: 0,
        averageScore: 0,
      };
    }
  }

  /**
   * Clear all progress and attempts (for testing)
   */
  clearAll(): void {
    localStorage.removeItem(this.STORAGE_KEY_PROGRESS);
    localStorage.removeItem(this.STORAGE_KEY_ATTEMPTS);
  }
}

// Singleton instance
export const workshopProgressTracker = new WorkshopProgressTracker();

